import { Avatar, Checkbox, Group, Icon, Text } from "@assetwatch/ui";
import type { WorkOrderId } from "../../lib/workIds";
import { workOrderDueLabel } from "../../lib/workOrderDueLabel";
import type { WorkOrder } from "../../queries/useWorkOrdersQuery";
import { PriorityIndicator } from "../snowflakes/PriorityIndicator";
import { UnreadIndicator } from "../snowflakes/UnreadIndicator";
import { WorkListStatusChip } from "../snowflakes/WorkListStatusChip";
import {
  displayWorkOrderNumber,
  formatWorkOrderCardNumber,
  workOrderListStatusTone,
} from "../snowflakes/work-orders/presentation";

const dueTextTone = {
  overdue: "negative",
  today: "warning",
  upcoming: "subtle",
  completed: "subtle",
  none: "subtle",
} as const;

export function WorkOrderRow({
  workOrder,
  selected,
  isChecked,
  facilityTimeZone,
  onSelect,
  onToggleChecked,
}: {
  workOrder: WorkOrder;
  selected: boolean;
  isChecked: boolean;
  facilityTimeZone?: string;
  onSelect: (workOrderId: WorkOrderId) => void;
  onToggleChecked: (workOrderId: WorkOrderId, checked: boolean) => void;
}) {
  const statusTone =
    workOrderListStatusTone[workOrder.status.code] ?? "neutral";
  const due = workOrderDueLabel(
    workOrder.dueDate,
    workOrder.status.code,
    facilityTimeZone,
    undefined,
    workOrder.completedOn,
  );
  const assignees = [...(workOrder.assignees ?? [])];
  if (assignees.length === 0 && workOrder.assignee)
    assignees.push(workOrder.assignee);
  return (
    <div
      className={`group flex w-full items-start gap-(--spacing-12) border-b border-outline-subtle px-(--spacing-16) py-(--spacing-16) transition-colors hover:bg-surface-container-low ${selected ? "border-l-2 border-l-primary bg-surface-container-low" : "border-l-2 border-l-transparent"}`}
    >
      <div className="pt-(--spacing-4)">
        <Checkbox
          aria-label={`Select Work Order ${displayWorkOrderNumber(workOrder.number)}`}
          checked={isChecked}
          onCheckedChange={(checked: boolean | "indeterminate") =>
            onToggleChecked(workOrder.id, Boolean(checked))
          }
        />
      </div>
      <button
        aria-label={`${workOrder.isUnread ? "Unread, " : ""}${workOrder.title}`}
        aria-pressed={selected}
        className="flex min-w-0 flex-1 items-start gap-(--spacing-12) border-none bg-transparent p-0 text-left"
        onClick={() => onSelect(workOrder.id)}
        type="button"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-(--spacing-4)">
          <Group align="center" gap={8} justify="between" wrap="nowrap">
            <div className="flex min-w-0 items-center gap-(--spacing-8)">
              <div className="min-w-0">
                <Text
                  size="lg"
                  truncate
                  weight={workOrder.isUnread ? "bold" : "regular"}
                >
                  {workOrder.title}
                </Text>
              </div>
              {workOrder.isUnread ? (
                <>
                  <UnreadIndicator dataSlot="work-order-unread-indicator" />
                  <span className="sr-only">Unread, </span>
                </>
              ) : null}
            </div>
            {assignees.length > 0 ? (
              <>
                {/* @ds-deviation: the design's list avatar is 24px and a
                    colored circle with initials, but Avatar only offers md
                    (40px) and xs (20px) in the neutral inverse color; xs is the
                    closest and is hidden from assistive tech, so the assignee
                    name is exposed through screen-reader text. Promote into the
                    design system: add a 24px Avatar size and a color option. */}
                <Group align="center" gap={4} wrap="nowrap">
                  {assignees.slice(0, 4).map((assignee) => (
                    <Avatar
                      key={`${assignee.id}:${assignee.name}`}
                      initials={assignee.initials}
                      size="xs"
                      variant="internal"
                    />
                  ))}
                  {assignees.length > 4 ? (
                    <Avatar
                      initials={`+${assignees.length - 4}`}
                      size="xs"
                      variant="internal"
                    />
                  ) : null}
                </Group>
                <span className="sr-only">
                  Assigned to{" "}
                  {assignees.map((assignee) => assignee.name).join(", ")}
                </span>
              </>
            ) : (
              <span
                aria-label="Unassigned"
                className="inline-flex size-(--spacing-20) shrink-0 items-center justify-center rounded-full border border-dashed border-outline text-text-subtle"
                role="img"
              >
                <Icon aria-hidden name="user" size="xs" />
              </span>
            )}
          </Group>
          <div className="flex min-w-0 items-center justify-between gap-(--spacing-8)">
            <div className="min-w-0 truncate">
              <Text as="span" size="sm" tone="subtle" truncate>
                {workOrder.asset?.name ?? "No asset"}
              </Text>
            </div>
            <span className="shrink-0 font-(family-name:--font-family-mono) text-(length:--font-size-body-small) leading-(--font-line-height-16) text-text-subtle tabular-nums">
              {formatWorkOrderCardNumber(workOrder.number)}
            </span>
          </div>
          <div className="mt-(--spacing-4)">
            <Group align="center" gap={8} justify="between" wrap="wrap">
              <WorkListStatusChip
                label={workOrder.status.name}
                showChevron={false}
                tone={statusTone}
              />
              <Group align="center" gap={8} justify="end" wrap="wrap">
                {due ? (
                  <span className="inline-flex shrink-0 items-center gap-(--spacing-4)">
                    {due.tone === "overdue" ? (
                      <Icon
                        aria-hidden
                        name="clock-seven"
                        size="xs"
                        tone="negative"
                      />
                    ) : null}
                    <Text
                      as="span"
                      size="sm"
                      tone={dueTextTone[due.tone]}
                      weight={due.tone === "overdue" ? "semibold" : "medium"}
                    >
                      {due.text}
                    </Text>
                  </span>
                ) : null}
                {workOrder.priority ? (
                  <PriorityIndicator priority={workOrder.priority.code} />
                ) : null}
              </Group>
            </Group>
          </div>
        </div>
      </button>
    </div>
  );
}
